import ERC20_ABI from 'abis/erc20.json'
import { useConnection } from 'hooks/useConnection'
import { calculateRewardRatePerSecond } from 'pages/KodiakFarmsDeploy/helper'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Address } from 'viem'

interface TokenBalance {
  hasBalance: boolean
  balance: bigint
  requiredAmount: bigint
}

interface TokenBalances {
  [tokenAddress: string]: TokenBalance
}

interface RewardToken {
  token: string
  amount: string
  period: number
  decimals: number
}

interface BalanceState {
  balances: TokenBalances
  isLoading: boolean
  error: string | null
  hasInsufficientBalance: boolean
  insufficientTokens: string[]
}

const INITIAL_STATE: BalanceState = {
  balances: {},
  isLoading: false,
  error: null,
  hasInsufficientBalance: false,
  insufficientTokens: [],
}

export function useRewardBalances(rewards: RewardToken[]) {
  const { account, wagmiPublicClient } = useConnection()
  const [state, setState] = useState<BalanceState>(INITIAL_STATE)
  const prevRewardsRef = useRef<string>('')

  // Debug logging for rewards changes
  useEffect(() => {
    const rewardsKey = JSON.stringify(rewards)
    prevRewardsRef.current = rewardsKey
  }, [rewards])

  const memoizedRewards = useMemo(() => {
    const filteredRewards = rewards.filter((reward) => reward.token && reward.amount)
    return filteredRewards
  }, [rewards])

  const getTokenBalance = useCallback(
    async (tokenAddress: string, amount: string, period: number, decimals: number): Promise<TokenBalance | null> => {
      try {
        if (!wagmiPublicClient || !account) return null

        const balance = await wagmiPublicClient.readContract({
          address: tokenAddress as Address,
          abi: ERC20_ABI,
          functionName: 'balanceOf',
          args: [account as Address],
        })

        const balanceBigInt = BigInt(balance as bigint)
        const ratePerSecond = calculateRewardRatePerSecond(amount, period, decimals)
        const requiredAmount = ratePerSecond * BigInt(period)

        return {
          hasBalance: balanceBigInt >= requiredAmount,
          balance: balanceBigInt,
          requiredAmount,
        }
      } catch (error) {
        return null
      }
    },
    [account, wagmiPublicClient]
  )

  const checkBalances = useCallback(async () => {
    if (!account || !wagmiPublicClient || !memoizedRewards.length) {
      setState((prev) => ({
        ...prev,
        error: 'Invalid configuration',
        isLoading: false,
      }))
      return
    }

    setState((prev) => ({
      ...prev,
      isLoading: true,
      error: null,
    }))

    try {
      const results = await Promise.all(
        memoizedRewards.map(async ({ token, amount, period, decimals }) => {
          const balance = await getTokenBalance(token, amount, period, decimals)
          return { token, balance }
        })
      )

      const newBalances: TokenBalances = {}
      const insufficientTokens: string[] = []

      results.forEach(({ token, balance }) => {
        if (balance) {
          newBalances[token] = balance
          if (!balance.hasBalance) {
            insufficientTokens.push(token)
          }
        }
      })

      setState({
        balances: newBalances,
        isLoading: false,
        error: null,
        hasInsufficientBalance: insufficientTokens.length > 0,
        insufficientTokens,
      })
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: 'Failed to check balances',
        isLoading: false,
      }))
    }
  }, [account, wagmiPublicClient, memoizedRewards, getTokenBalance])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      checkBalances()
    }, 1000)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [checkBalances])

  return state
}
